// Where to go once the user is authenticated: the page a guard sent them away
// from. Carried as ?returnUrl= on /signin (the existing contract, kept through
// a wrong password since the URL does not change) and mirrored in
// sessionStorage for the flows that leave /signin (sign up -> activation).
// Only same-origin in-app paths are ever used (getSafeReturnUrl), never an
// auth page (no signin -> destination -> signin loop), and a destination is
// consumed once. Whether the role may open it is still decided by the route
// guards themselves, which fall back to their own safe page.
import { getSafeReturnUrl, RETURN_URL_QUERY_PARAM } from "../../utils/userAddingRoleToken";

export const POST_AUTH_DESTINATION_KEY = "elakay:postAuthDestination";
const MAX_AGE_MS = 30 * 60 * 1000;
const AUTH_FLOW_PATH = /^\/(signin|signup|forget-password|reset-password|password|active)(\/|\?|#|$)/i;

export const safePostAuthDestination = (value) => {
  const safe = getSafeReturnUrl(value);
  return safe && !AUTH_FLOW_PATH.test(safe) ? safe : null;
};

export const rememberPostAuthDestination = (value) => {
  const destination = safePostAuthDestination(value);
  if (!destination) return null;
  try { window.sessionStorage.setItem(POST_AUTH_DESTINATION_KEY, JSON.stringify({ destination, at: Date.now() })); } catch (_) { /* storage blocked */ }
  return destination;
};

// /signin for a visitor stopped on `location` (pathname + search + hash).
export const signinPathFor = (location) => {
  const destination = rememberPostAuthDestination(`${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`);
  return destination && destination !== "/" ? `/signin?${RETURN_URL_QUERY_PARAM}=${encodeURIComponent(destination)}` : "/signin";
};

// The destination to open after a successful authentication, removed so it
// is used only once: ?returnUrl= of the current page first, else the one
// remembered in this tab (if recent), else `fallback`.
export const consumePostAuthDestination = (fallback = "/") => {
  let fromQuery = null;
  try { fromQuery = safePostAuthDestination(new URLSearchParams(window.location.search).get(RETURN_URL_QUERY_PARAM)); } catch (_) { /* no window */ }
  let remembered = null;
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(POST_AUTH_DESTINATION_KEY) || "null");
    if (stored && Date.now() - Number(stored.at) < MAX_AGE_MS) remembered = safePostAuthDestination(stored.destination);
    window.sessionStorage.removeItem(POST_AUTH_DESTINATION_KEY);
  } catch (_) { /* storage blocked or corrupt */ }
  return fromQuery || remembered || fallback;
};
